import { useState, useId, forwardRef, useRef, useEffect, ReactNode, FC } from 'react';
import { Input } from "../Input";

export type SelectProps = {
    options: Array<any>;
    selectedOption?: any;
    onClick?: () => void;
    onSelect?: (selected: any) => void;
    name?: string;
    id?: string;
    form?: string;
    required?: boolean;
    placeholder?: string;
    disabled?: boolean;
    error?: string;
    label?: string;
    dropdownColor?: string;
    optionsColor?: string;
    showTick?: boolean;
    variant?: 'secondary' | 'plain' | 'primary';
    leadingIcon?: ReactNode;
    width?: string;
    borderColor?: string;
    className?: string;
    value?: string;
    state?: string;
    showSearch?: boolean;
    searchPlaceholder?: string;
}

export const Select: FC<SelectProps> = forwardRef<HTMLDivElement, SelectProps>((props, ref) => {
    const {
        options = [],
        selectedOption,
        onClick = () => { },
        onSelect = () => { },
        id,
        required = false,
        placeholder = 'Select',
        disabled = false,
        error,
        label,
        showTick = true,
        variant = 'primary',
        leadingIcon,
        width = 'w-full',
        borderColor = 'border-none',
        className = 'w-auto',
        value = 'key',
        state = 'state',
        showSearch = true, //  frontend filter only 
        searchPlaceholder = 'Search...',
    } = props;
    const uniqueId = useId();
    const dropdownId = id || `dropdown-${uniqueId}`;
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [filteredOptions, setFilteredOptions] = useState(options);
    const combinedRef = ref || dropdownRef;

    const [isOpen, setIsOpen] = useState(false);
    const [selected, setSelected] = useState<any | undefined>(selectedOption);
    const [search, setSearch] = useState('');

    useEffect(() => {
        const selectedOptionFromOptions = options.find(option => option[value] === selectedOption || option[state] === selectedOption);
        setSelected(selectedOptionFromOptions);
    }, [selectedOption, options, value, state]);

    const toggleDropdown = () => {
        setIsOpen(!isOpen)
        onClick?.()
    };

    const handleSelect = (option: any) => {
        setSelected(option);
        onSelect?.(option);
        setIsOpen(false);
        setSearch('');
    };

    useEffect(() => {
        setFilteredOptions(options.filter(option => option[state].toLowerCase().includes(search.toLowerCase())));
    }, [search, options, state]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [dropdownRef]);

    const variants = {
        primary: '',
        secondary: 'text-white bg-black border border-[#D0D5DD]',
        plain: 'border-transparent',
    }

    return (
        <div className={`relative flex flex-col gap-1 ${width} ${className}`} ref={combinedRef}>
            {label && (
                <label htmlFor={dropdownId} className={`block text-sm font-medium ${variant === 'primary' ? '' : 'text-[#D0D5DD]'}`}>
                    {label}{required && <span className="text-red-500 ml-1">*</span>}
                </label>
            )}

            <div className="relative">
                <button
                    type="button"
                    className={`grid sm:text-sm/5 w-full cursor-pointer grid-cols-1 rounded-md px-2.5 py-2 text-left ${variants[variant] || ''} focus-within:outline focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-primary-700 focus-within:ring-0 focus-within:border-transparent 
                    ${variant === 'primary' ? 'bg-white' : 'bg-transparent'}
                    ${error ? "outline !outline-2 -outline-offset-2 outline-red-500" : "border border-secondary-20"}`}
                    aria-haspopup="listbox"
                    aria-expanded={isOpen}
                    aria-labelledby="listbox-label"
                    onClick={toggleDropdown}
                    disabled={disabled}
                >
                    <div className='flex justify-between items-center gap-2'>
                        <span className="flex items-center justify-center gap-2">
                            {leadingIcon && (
                                <div className="flex items-center pointer-events-none">
                                    {leadingIcon}
                                </div>
                            )}
                            <span className={`flex justify-center items-center truncate ${selected ? variant === 'primary' ? 'text-black' : 'text-white' : 'text-secondary-20'}`}>{selected?.[state] || placeholder}</span>
                        </span>
                        <svg width="20" height="20" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d={isOpen ? "M6.5 15L12.5 9L18.5 15" : "M6.5 9L12.5 15L18.5 9"}
                                stroke={variant === 'primary' ? '#D0D5DD' : '#EAECF0'}
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                </button>

                {isOpen && (
                    <ul
                        className={`absolute top-full z-10 mt-1 min-h-[100px] max-h-[250px] overflow-y-auto overflow-x-hidden w-full line-clamp-1 rounded-md text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm ${variant === 'primary' ? 'bg-white' : 'bg-[#1B1B1B]'}`}
                        tabIndex={-1}
                        role="listbox"
                        aria-labelledby="listbox-label"
                    >
                        {showSearch && (
                            <li className={`py-2 pl-3 pr-3 ${variant === 'primary' ? 'bg-white' : 'bg-[#1B1B1B]'}`}>
                                <div className='flex items-center'>
                                    {/* <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} className='outline-none w-full text-white bg-transparent border-none' placeholder={searchPlaceholder} /> */}

                                    <Input
                                        variant={variant}
                                        type="text"
                                        name="name"
                                        placeholder={searchPlaceholder}
                                        maxLength={20}
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        className={`w-full ${variant === 'primary' ? 'bg-white' : 'bg-[#1B1B1B]'}`}
                                    />
                                </div>
                            </li>
                        )}
                        {filteredOptions.length === 0 && search.length > 0 && (
                            <li className='py-2 pl-3 pr-3'>
                                <div className='flex items-center'>
                                    <span className='text-secondary-20'>No matching for <b>{search}</b></span>
                                </div>
                            </li>
                        )}

                        {filteredOptions.map((option) => (
                            <li
                                key={option[value] as string}
                                className={`relative cursor-default select-none py-2 pl-3 pr-9 ${selected?.[value] === option[value]
                                    ? variant === 'primary' ? 'bg-gray-200 text-black' : 'bg-gray-800 text-white'
                                    : variant === 'primary' ? 'bg-white text-black' : 'bg-[#1B1B1B] text-white'
                                    }`}
                                onClick={() => handleSelect(option)}
                            >
                                <div className="flex items-center">
                                    {option.avatar && (
                                        <img src={option?.avatar} srcSet={`${option?.avatar} 1x, ${option?.avatar} 2x`} loading="lazy" alt="" className="size-5 shrink-0 rounded-full" />
                                    )}
                                    <span className={`ml-3 block truncate ${selected?.[value] === option[value] ? 'font-semibold' : 'font-normal'}`}>
                                        {option[state]}
                                    </span>
                                </div>

                                {showTick && selected?.[value] === option[value] && (
                                    <span className="absolute inset-y-0 right-0 flex items-center px-2 text-indigo-600">
                                        <svg className="size-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                            <path
                                                fillRule="evenodd"
                                                d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                    </span>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
            {error && <p className="absolute -bottom-4 text-sm leading-none text-red-500">{error}</p>}
        </div>
    );
});

Select.displayName = 'Select';